import {
  FAIcon,
  TextLink,
  TextLinkIconPosition,
  TextLinkStyle,
  useTranslation,
  Modal,
} from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  AmwayUserDetails,
  ApiProjections,
  Cart,
  CartConstantsEnum,
  CartEntryDetails,
  CartEventsEnum,
  OrderType,
  PublishEvent,
  SWITCH_PROXY_TYPE,
  cartApi,
  getActiveCart,
  isProxyOrder,
  switchCart,
  switchOrOverrideCart,
  switchToRegularCart,
  updateCart,
  updateShippingAddressInLS,
  updateShippingAddressToEmpty,
  validateBundleInEntries,
} from '@commerce-cart-webcomponents/sdk';
import { useAddressUpdate } from '@commerce-webcomponents/ui';
import { Accordion } from '../accordion/Accordion';
import { CartTypeSelection } from '../../../components/proxy-order-banner/cart-type-selection-drawer/cart-type-selection/CartTypeSelection';
import { AccountSearchDrawer } from '../account-search-drawer/AccountSearchDrawer';
import { useCartNotification } from '../../hooks/useCartNotification';
import { CartNotificationTheme } from '../cart-notification/types/enums';
import { TOAST_TIMEOUT } from '../../types/enums';
import styles from './ChangeOrderType.module.scss';
import { type ChangeOrderTypeProps } from './types/propsType';

export const ChangeOrderType = ({
  cartData,
  onRadioChange,
  productDetails = [],
  onSuccessCB,
  onConfirmClickCB,
}: ChangeOrderTypeProps) => {
  const t = useTranslation();
  const { updateAddress } = useAddressUpdate();
  const { createToast, deleteToast } = useCartNotification('changeOrderType');
  const dispatch = useDispatch();
  const [showContent, setShowContent] = useState(false);
  const [orderType, setOrderType] = useState<OrderType>(OrderType.Gco);
  const [showAccountSearch, setShowAccountSearch] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AmwayUserDetails>();

  const multipleCartText = t('cart.changeOrderType.multipleCart');
  const searchDrawerHeaderTitle = t('cart.cartTypeSelectionDrawer.drawerHeader');
  const inputPlaceHolderText = t('cart.cartTypeSelectionDrawer.inputPlaceHolder');
  const primaryButtonText = t('cart.cartTypeSelectionDrawer.primaryButtonText');
  const secondaryButtonText = t('cart.cartTypeSelectionDrawer.secondaryButtonText');
  const subHeadingText = t('cart.cartTypeSelectionDrawer.subHeading');
  const successToast = t('cart.changeOrderType.successToast');

  const orderForSelfText = t('cart.changeOrderType.orderForSelf');
  const orderForSelfAlertText = t('cart.changeOrderType.orderForSelfAlert');
  const continueText = t('cart.cartTypeSelectionDrawer.continue');
  const cancelText = t('cart.cartTypeSelectionDrawer.cancel');

  const showSuccessToast = () => {
    createToast(
      {
        theme: CartNotificationTheme.SUCCESS,
        rightSeperator: false,
        content: successToast,
        clickIcon: 'times',
        icon: 'check-circle',
        timeOut: TOAST_TIMEOUT,
      },
      deleteToast,
    );
  };

  const handleTextLinkClick = () => {
    setShowContent((d) => !d);
  };

  const handleRadioChange = (orderType: OrderType) => {
    setOrderType(orderType);
    onRadioChange?.(orderType);

    if (orderType === OrderType.Proxy) {
      setShowAccountSearch(true);
    }

    if (orderType === OrderType.Regular) {
      setShowModal(true);
    }
  };

  const handleSecondayClickModal = () => {
    setShowModal(false);
    setOrderType(OrderType.Gco);
  };

  const handleAccountSearchCloseClick = () => {
    setShowAccountSearch(false);
    setOrderType(OrderType.Gco);
  };

  const handleAccountSearchConfirmClick = async () => {
    if (orderType === OrderType.Proxy) {
      const res = await switchCart(cartData, OrderType.Proxy, selectedProfile as AmwayUserDetails);
      await addToCart(res, res.subCarts[0].subCartId as string);
      setShowAccountSearch(false);
      await updateShippingAddressInLS(
        res.subCarts?.[0].amwayUser?.amwayUserId as string,
        res.subCarts?.[0].amwayUser?.accountId as string,
      );
      updateAddress();
      onSuccessCB?.();
      showSuccessToast();
    }
  };

  const addToCart = async (cart: Cart, subCartId: string) => {
    onConfirmClickCB?.();
    const entries = productDetails?.map(({ productId, quantity = 1, bundleSlots }) => ({
      product: { id: productId },
      quantity,
      bundleSlots,
    })) as CartEntryDetails[];
    const payload = {
      subCarts: [
        {
          subCartId: subCartId,
          entries: validateBundleInEntries(entries),
        },
      ],
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };
    const res = await cartApi.addToCart({
      cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
      subCartId: subCartId,
      projections: ApiProjections.DETAILED,
      payload,
    });
    PublishEvent({
      cartEvent: CartEventsEnum.UpdateMiniCartIcon,
      cart: res as Cart,
    });
    dispatch(updateCart(res as Cart));
  };

  const handlePrimaryClick = async () => {
    if (orderType === OrderType.Regular) {
      const res = (await switchToRegularCart(cartData)) as Cart;
      await addToCart(res, res.id);
      const addRes = await updateShippingAddressInLS(
        res.amwayUser.amwayUserId as string,
        res.amwayUser.accountId as string,
      );
      if (res === null || addRes?.contacts.length === 0) {
        updateShippingAddressToEmpty();
      }
      updateAddress();
      onSuccessCB?.();
      showSuccessToast();
      setShowModal(false);
    }
  };

  const handleModalClick = async (switchCartType?: SWITCH_PROXY_TYPE) => {
    const previousOrderType = cartData.orderType;
    const res = (await switchOrOverrideCart(
      getActiveCart(cartData),
      selectedProfile as AmwayUserDetails,
      switchCartType as SWITCH_PROXY_TYPE,
    )) as Cart;
    const proxyCart = getActiveCart(res);
    const addressRes = await updateShippingAddressInLS(
      proxyCart.amwayUser?.amwayUserId as string,
      proxyCart.amwayUser?.accountId as string,
    );
    if (isProxyOrder(previousOrderType) && (addressRes === null || !addressRes?.contacts.length)) {
      //If api return empty res or fails while switching to proxy revert back to main user default address.
      const res = await updateShippingAddressInLS(
        cartData.amwayUser?.amwayUserId,
        cartData.amwayUser?.accountId as string,
      );
      if (res === null || res?.contacts.length === 0) {
        updateShippingAddressToEmpty();
      }
    }
    updateAddress();
    addToCart(res, proxyCart.subCartId as string);
  };

  return (
    <>
      {showModal && (
        <Modal
          headline={orderForSelfText}
          primaryButtonLabel={continueText}
          secondaryButtonLabel={cancelText}
          onSecondaryButtonClick={handleSecondayClickModal}
          onPrimaryButtonClick={handlePrimaryClick}
          onClose={handleSecondayClickModal}
        >
          <>{orderForSelfAlertText}</>
        </Modal>
      )}
      {showAccountSearch && (
        <AccountSearchDrawer
          showSwitchModal={true}
          modalConfirmClick={handleModalClick}
          showActiveCart={true}
          onRadioChange={setSelectedProfile}
          primaryButtonText={primaryButtonText}
          secondaryButtonText={secondaryButtonText}
          placeHolderText={inputPlaceHolderText}
          drawerHeaderTitle={searchDrawerHeaderTitle}
          subHeading={subHeadingText}
          onPrimaryButtonClick={handleAccountSearchCloseClick}
          onSecondaryClick={handleAccountSearchConfirmClick}
          cartData={cartData}
          hidePrimaryButton={true}
        />
      )}
      <Accordion
        className={styles['change-order-type-accordion']}
        expand={showContent}
        title={
          <div
            tabIndex={0}
            role="button"
            aria-label="change-order-type-title"
            onKeyDown={handleTextLinkClick}
            onClick={handleTextLinkClick}
            className={styles['change-order-type-title']}
          >
            <div className={styles['change-order-type-title-left']}>
              <FAIcon iconName="user-group" />
              <span>{multipleCartText}</span>
            </div>
            <div className={styles['change-order-type-title-right']}>
              {!showContent && (
                <TextLink
                  onClick={(e) => e.preventDefault()}
                  customClass={styles['change-order-type-title-right-link']}
                  iconPosition={TextLinkIconPosition.RIGHT}
                  textLinkStyle={TextLinkStyle.UNDERLINE}
                  text={t('cart.proxyOrderBanner.change')}
                />
              )}
              <FAIcon iconName={showContent ? 'chevron-up' : 'chevron-down'} />
            </div>
          </div>
        }
      >
        <div className={styles['change-order-type-content']}>
          <CartTypeSelection
            onRadioChange={handleRadioChange}
            cardClassName={styles['change-order-type-content-card']}
            initalOrderType={orderType}
            cartData={cartData}
          />
        </div>
      </Accordion>
    </>
  );
};
